import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { 
  useJob,
  useJobDetailedAnalytics, 
  useUpdateJobStatus, 
  useToggleFeaturedStatus 
} from '../../hooks/unified/useJobsQueries';
import { useJobApplications } from '../../hooks/unified/useAdminQueries';
import { useGenerateSocialPost } from '../../hooks/unified/useAIQueries';
import { useCancelInterview } from '../../hooks/unified/useInterviewsQueries';
import { useToast } from '../../contexts/ToastContext';
import { unifiedApiService } from '../../services/unified';

interface ScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  existingInterview?: any;
}

function ScheduleInterviewModal({ 
  isOpen, 
  onClose, 
  applicationId, 
  candidateName,
  candidateEmail,
  jobTitle,
  existingInterview 
}: ScheduleInterviewModalProps) {
  const { showSuccess, showError } = useToast();
  const cancelInterviewMutation = useCancelInterview();
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [interviewType, setInterviewType] = useState('Technical Interview');
  const [panelEmail, setPanelEmail] = useState('');
  const [panelName, setPanelName] = useState('');
  const [panelRole, setPanelRole] = useState('');
  const [panelMembers, setPanelMembers] = useState<Array<{ email: string; name: string; role: string }>>([]);
  const [notes, setNotes] = useState('');
  const [createGoogleMeet, setCreateGoogleMeet] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdInterview, setCreatedInterview] = useState<any>(existingInterview || null);
  const [meetLink, setMeetLink] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load existing interview data
  React.useEffect(() => {
    if (existingInterview) {
      const dateTime = new Date(existingInterview.scheduled_date);
      setScheduledDate(dateTime.toISOString().split('T')[0]);
      setScheduledTime(dateTime.toTimeString().slice(0, 5));
      setDuration(existingInterview.duration_minutes);
      setInterviewType(existingInterview.interview_type);
      setNotes(existingInterview.notes || '');
      setMeetLink((existingInterview as any).google_meet_link || '');
      
      // Parse panel members if available
      if (existingInterview.panel_members) {
        try {
          const members = typeof existingInterview.panel_members === 'string' 
            ? JSON.parse(existingInterview.panel_members)
            : existingInterview.panel_members;
          setPanelMembers(members);
        } catch (e) {
          console.error('Failed to parse panel members:', e);
        }
      }
    }
  }, [existingInterview]);

  const addPanelMember = () => {
    if (panelEmail && panelName) {
      setPanelMembers([...panelMembers, { email: panelEmail, name: panelName, role: panelRole }]);
      setPanelEmail('');
      setPanelName('');
      setPanelRole('');
    }
  };

  const removePanelMember = (index: number) => {
    setPanelMembers(panelMembers.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!scheduledDate || !scheduledTime) {
      showError('Please fill in date and time');
      return;
    }

    setIsSubmitting(true);
    try {
      // User inputs IST time, add IST timezone offset (+05:30)
      const scheduledDateTime = `${scheduledDate}T${scheduledTime}:00+05:30`;
      
      console.log('📅 Interview datetime:', {
        userInput: `${scheduledDate}T${scheduledTime}`,
        sentToBackend: scheduledDateTime,
        note: 'User inputs IST time, we add +05:30 timezone'
      });
      
      let interview;
      if (existingInterview && !isEditing) {
        // If reopening existing interview, just show it
        interview = existingInterview;
      } else if (existingInterview && isEditing) {
        // Update existing interview
        interview = await unifiedApiService.interviews.updateInterview(
          existingInterview.id,
          {
            scheduled_date: scheduledDateTime,
            duration_minutes: duration,
            interview_type: interviewType,
            panel_members: panelMembers,
            notes,
          }
        );
        showSuccess('Interview updated successfully!');
      } else {
        // Create new interview
        interview = await unifiedApiService.interviews.scheduleInterview({
          application_id: applicationId,
          scheduled_date: scheduledDateTime,
          duration_minutes: duration,
          interview_type: interviewType,
          panel_members: panelMembers,
          notes,
          create_google_meet: createGoogleMeet,
        });
        showSuccess('Interview scheduled successfully! Email notifications have been sent.');
      }

      setCreatedInterview(interview);
      setMeetLink((interview as any).google_meet_link || '');
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to schedule/update interview:', error);
      showError('Failed to schedule/update interview. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    if (meetLink) {
      navigator.clipboard.writeText(meetLink);
      showSuccess('Meeting link copied to clipboard!');
    } else {
      showError('No meeting link available. Google Meet was not configured or creation failed.');
    }
  };

  const handleDeleteInterview = async () => {
    if (!createdInterview?.id) return;

    try {
      await cancelInterviewMutation.mutateAsync(createdInterview.id);
      showSuccess('Interview deleted successfully!');
      setShowDeleteConfirm(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to delete interview:', error);
      showError('Failed to delete interview. Please try again.');
    }
  };

  const handleClose = () => {
    if (createdInterview) {
      window.location.reload();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 sm:top-10 mx-2 sm:mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-2 sm:mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">
              {createdInterview ? 'Interview Scheduled' : 'Schedule Interview'}
            </h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-800 break-words">
                <strong>Candidate:</strong> {candidateName} <span className="block sm:inline">({candidateEmail})</span>
              </p>
              <p className="text-sm text-blue-800">
                <strong>Position:</strong> {jobTitle}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration *</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interview Type *</label>
                <select
                  value={interviewType}
                  onChange={(e) => setInterviewType(e.target.value)}
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Technical Interview">Technical Interview</option>
                  <option value="HR Interview">HR Interview</option>
                  <option value="Behavioral Interview">Behavioral Interview</option>
                  <option value="Final Round">Final Round</option>
                  <option value="Phone Screening">Phone Screening</option>
                </select>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2 min-h-[44px]">
                <input
                  type="checkbox"
                  checked={createGoogleMeet}
                  onChange={(e) => setCreateGoogleMeet(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                />
                <span className="text-sm font-medium text-gray-700">Create Google Meet Link</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Panel Members (Optional)</label>
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="email"
                    placeholder="Email *"
                    value={panelEmail}
                    onChange={(e) => setPanelEmail(e.target.value)}
                    className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Name *"
                    value={panelName}
                    onChange={(e) => setPanelName(e.target.value)}
                    className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Role"
                    value={panelRole}
                    onChange={(e) => setPanelRole(e.target.value)}
                    className="px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={addPanelMember}
                  type="button"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium min-h-[44px] px-2"
                >
                  + Add Panel Member
                </button>
              </div>

              {panelMembers.length > 0 && (
                <div className="mt-3 space-y-2">
                  {panelMembers.map((member, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="text-sm">
                        <span className="font-medium">{member.name}</span>
                        <span className="text-gray-500"> ({member.email})</span>
                        {member.role && <span className="text-gray-500"> - {member.role}</span>}
                      </div>
                      <button
                        onClick={() => removePanelMember(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Additional notes or instructions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {!createdInterview || isEditing ? (
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 pt-4 border-t">
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
                >
                  {isSubmitting ? (isEditing ? 'Updating...' : 'Scheduling...') : (isEditing ? 'Update Interview' : 'Schedule Interview')}
                </button>
              </div>
            ) : (
              <div className="pt-4 border-t">
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-green-800 mb-1">Interview Scheduled Successfully!</h4>
                      <p className="text-sm text-green-700">
                        Interview scheduled for {new Date(createdInterview.scheduled_date).toLocaleString('en-IN', { 
                          timeZone: 'Asia/Kolkata',
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })} IST
                      </p>
                      {meetLink ? (
                        <div className="mt-2 p-3 bg-white rounded border border-green-200">
                          <p className="text-xs font-medium text-gray-600 mb-1">Google Meet Link:</p>
                          <a 
                            href={meetLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                          >
                            {meetLink}
                          </a>
                        </div>
                      ) : (
                        <div className="mt-2 p-3 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-xs text-yellow-800">
                            <strong>Note:</strong> Google Meet link was not created. Google OAuth is not configured. 
                            You can manually create a meeting link and share it with the candidate.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                  <div className="flex">
                    <button
                      onClick={handleCopyLink}
                      className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md w-full sm:w-auto"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {meetLink ? 'Copy Link' : 'No Meet Link'}
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={cancelInterviewMutation.isPending}
                      className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                    >
                      Done
                    </button>
                  </div>
                  
                  {/* Delete Confirmation Dialog */}
                  {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
                      <div className="bg-white rounded-t-xl sm:rounded-lg p-4 sm:p-6 w-full sm:max-w-md sm:mx-4 shadow-xl">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Delete Interview</h3>
                            <p className="text-sm text-gray-600 mb-4">
                              Are you sure you want to delete this interview? This action cannot be undone.
                            </p>
                            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:space-x-3 sm:justify-end">
                              <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={cancelInterviewMutation.isPending}
                                className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleDeleteInterview}
                                disabled={cancelInterviewMutation.isPending}
                                className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                              >
                                {cancelInterviewMutation.isPending ? 'Deleting...' : 'Delete Interview'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SocialMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
}

function SocialMediaModal({ isOpen, onClose, jobId, jobTitle }: SocialMediaModalProps) {
  const [platform, setPlatform] = useState<'instagram_square' | 'linkedin_post' | 'twitter_post' | 'facebook_post'>('linkedin_post');
  const [style, setStyle] = useState<'professional' | 'modern' | 'creative' | 'minimalist'>('professional');
  const generateSocialPost = useGenerateSocialPost();

  const handleGenerate = async () => {
    try {
      const result = await generateSocialPost.mutateAsync({
        job_id: jobId,
        platform,
        style,
      });
      
      // Download the image
      const link = document.createElement('a');
      link.href = result.download_url;
      link.download = `${jobTitle.replace(/\s+/g, '-')}-${platform}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      onClose();
    } catch (error) {
      console.error('Failed to generate social post:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end sm:items-start justify-center">
      <div className="relative sm:top-20 w-full sm:max-w-md mx-0 sm:mx-auto p-4 sm:p-5 border shadow-lg rounded-t-xl sm:rounded-md bg-white">
        <div className="mt-2 sm:mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Generate Social Media Post</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as any)}
                className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="linkedin_post">LinkedIn (1200x627)</option>
                <option value="instagram_square">Instagram Square (1080x1080)</option>
                <option value="twitter_post">Twitter (1200x675)</option>
                <option value="facebook_post">Facebook (1200x630)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as any)}
                className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="professional">Professional</option>
                <option value="modern">Modern</option>
                <option value="creative">Creative</option>
                <option value="minimalist">Minimalist</option>
              </select>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generateSocialPost.isPending}
                className="px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
              >
                {generateSocialPost.isPending ? 'Generating...' : 'Generate & Download'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminJobDetailView() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [candidateFilter, setCandidateFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'stage'>('date');
  const [interviewsMap, setInterviewsMap] = useState<Record<string, any>>({});

  // Fetch job details, analytics and applications
  const { data: job, isLoading: jobLoading } = useJob(jobId || '');
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = useJobDetailedAnalytics(jobId || '');
  const { data: applications, isLoading: candidatesLoading } = useJobApplications(jobId || '');

  // Fetch interviews for this job
  React.useEffect(() => {
    const fetchInterviews = async () => {
      if (jobId) {
        try {
          const interviews = await unifiedApiService.interviews.getJobInterviews(jobId);
          const map: Record<string, any> = {};
          interviews.forEach((interview: any) => {
            map[interview.application_id] = interview;
          });
          setInterviewsMap(map);
        } catch (error) {
          console.error('Failed to fetch interviews:', error);
        }
      }
    };
    fetchInterviews();
  }, [jobId]);
  
  // Mutations
  const updateJobStatus = useUpdateJobStatus();
  const toggleFeatured = useToggleFeaturedStatus();

  if (jobLoading || analyticsLoading || candidatesLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (analyticsError || !analytics) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load job details</p>
          <button
            onClick={() => navigate('/admin/jobs')}
            className="mt-4 text-primary-600 hover:text-primary-700"
          >
            Back to Jobs
          </button>
        </div>
      </AdminLayout>
    );
  }

  // Filter and sort applications
  const filteredApplications = applications?.filter(app => 
    candidateFilter === 'all' || app.status === candidateFilter
  ) || [];

  const sortedApplications = [...filteredApplications].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime();
    }
    return a.status.localeCompare(b.status);
  });

  const handleStatusChange = async (newStatus: 'draft' | 'active' | 'archived' | 'closed') => {
    try {
      await updateJobStatus.mutateAsync({ jobId: jobId!, status: newStatus });
    } catch (error) {
      console.error('Failed to update job status:', error);
    }
  };

  const handleToggleFeatured = async () => {
    try {
      await toggleFeatured.mutateAsync(jobId!);
    } catch (error) {
      console.error('Failed to toggle featured status:', error);
    }
  };

  const handleStageUpdate = async (applicationId: string, newStatus: string) => {
    try {
      await unifiedApiService.applications.updateApplicationStatus(applicationId, { status: newStatus });
      // Refetch data after update
      window.location.reload(); // Simple reload for now
    } catch (error) {
      console.error('Failed to update application status:', error);
    }
  };

  const handleAdvanceStage = async (applicationId: string, sendEmail: boolean = false) => {
    try {
      await unifiedApiService.applications.advanceApplicationStage(applicationId, sendEmail);
      showSuccess('Application advanced to next stage successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Failed to advance application stage:', error);
      showError('Failed to advance stage. The application may already be at the final stage.');
    }
  };

  const handleSendEmail = async (applicationId: string) => {
    try {
      await unifiedApiService.applications.sendApplicationEmail(applicationId);
      showSuccess('Email sent successfully!');
    } catch (error) {
      console.error('Failed to send email:', error);
      showError('Failed to send email. Please try again.');
    }
  };

  const handleScheduleInterview = (application: any) => {
    const existingInterview = interviewsMap[application.application_id];
    setSelectedApplication({ 
      ...application, 
      existingInterview 
    });
    setShowInterviewModal(true);
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/admin/jobs" className="text-gray-500 hover:text-gray-700">
                Jobs
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium">{analytics.job_title}</li>
          </ol>
        </nav>

        {/* Job Overview Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{job?.title || analytics?.job_title}</h1>
                {Boolean(job?.is_featured) && (
                  <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full whitespace-nowrap">
                    ⭐ Featured
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                <span className="truncate">ID: {job?.id || analytics?.job_id}</span>
                <span className="hidden sm:inline">•</span>
                <span>Status: <span className="font-medium capitalize">{job?.status || 'active'}</span></span>
                <span className="hidden sm:inline">•</span>
                <span>Posted: {job?.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
              <button
                onClick={() => navigate(`/admin/jobs/edit/${jobId}`)}
                className="h-10 min-h-[44px] px-3 sm:px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit Job
              </button>
              <button
                onClick={handleToggleFeatured}
                disabled={toggleFeatured.isPending}
                className={`h-10 min-h-[44px] px-3 sm:px-4 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                  Boolean(job?.is_featured)
                    ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {toggleFeatured.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    ...
                  </span>
                ) : Boolean(job?.is_featured) ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </span>
                ) : (
                  'Feature'
                )}
              </button>
              <select
                value={job?.status || 'active'}
                onChange={(e) => handleStatusChange(e.target.value as any)}
                disabled={updateJobStatus.isPending}
                className="h-10 min-h-[44px] px-3 sm:px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={() => setShowSocialModal(true)}
                className="h-10 min-h-[44px] px-3 sm:px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                Social Post
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Views</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2">{analytics.views || 0}</p>
              </div>
              <div className="p-2 sm:p-3 bg-primary-100 rounded-full">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Applications</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2">{analytics.applications || 0}</p>
              </div>
              <div className="p-2 sm:p-3 bg-success/10 rounded-full">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Conversion</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2">
                  {analytics.conversion_rate ? `${(analytics.conversion_rate * 100).toFixed(1)}%` : '0%'}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-warning/10 rounded-full">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Candidates</p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 sm:mt-2">
                  {Object.values(analytics.applications_by_stage || {}).reduce((sum, count) => sum + count, 0)}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-accent-100 rounded-full">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Applications by Stage */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Applications by Stage</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
            {(() => {
              const stageLabels: Record<string, { label: string; color: string }> = {
                submitted: { label: 'Submitted', color: 'gray' },
                reviewed: { label: 'Reviewed', color: 'yellow' },
                shortlisted: { label: 'Shortlisted', color: 'indigo' },
                interviewed: { label: 'Interviewed', color: 'purple' },
                offered: { label: 'Offered', color: 'cyan' },
                hired: { label: 'Hired', color: 'green' },
                rejected: { label: 'Rejected', color: 'red' },
              };
              const stages = analytics.applications_by_stage || {} as Record<string, number>;
              // Show all stages, even if count is 0
              return Object.entries(stageLabels).map(([key, { label, color }]) => {
                const count = (stages as Record<string, number>)[key] || (stages as Record<string, number>)[label] || 0;
                return (
                  <div key={key} className="border border-gray-200 rounded-lg p-2 sm:p-4 text-center">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{count}</p>
                    <p className={`text-[10px] sm:text-xs font-medium text-${color}-600 truncate`}>{label}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Trend Charts - Only show if there's meaningful data */}
        {((analytics.view_trend && analytics.view_trend.some(d => d.count > 0)) || 
          (analytics.application_trend && analytics.application_trend.some(d => d.count > 0))) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-4">View Trend (Last 7 Days)</h2>
              {analytics.view_trend && analytics.view_trend.some(d => d.count > 0) ? (
                <div className="h-40 sm:h-48 flex items-end justify-between space-x-1 sm:space-x-2">
                  {analytics.view_trend.slice(-7).map((day, index) => {
                    const maxCount = Math.max(...(analytics.view_trend?.map(d => d.count) || [1]), 1);
                    const height = Math.max((day.count / maxCount) * 100, day.count > 0 ? 8 : 2);
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center min-w-0">
                        <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{day.count}</div>
                        <div 
                          className={`w-full rounded-t ${day.count > 0 ? 'bg-blue-500' : 'bg-gray-200'}`}
                          style={{ height: `${height}%`, minHeight: '4px' }}
                          title={`${day.count} views`}
                        />
                        <p className="text-[10px] sm:text-xs text-gray-600 mt-2 truncate w-full text-center">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-40 sm:h-48 flex items-center justify-center text-gray-400 text-sm">
                  No view data yet
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-4">Application Trend (Last 7 Days)</h2>
              {analytics.application_trend && analytics.application_trend.some(d => d.count > 0) ? (
                <div className="h-40 sm:h-48 flex items-end justify-between space-x-1 sm:space-x-2">
                  {analytics.application_trend.slice(-7).map((day, index) => {
                    const maxCount = Math.max(...(analytics.application_trend?.map(d => d.count) || [1]), 1);
                    const height = Math.max((day.count / maxCount) * 100, day.count > 0 ? 8 : 2);
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center min-w-0">
                        <div className="text-[10px] sm:text-xs text-gray-500 mb-1">{day.count}</div>
                        <div 
                          className={`w-full rounded-t ${day.count > 0 ? 'bg-green-500' : 'bg-gray-200'}`}
                          style={{ height: `${height}%`, minHeight: '4px' }}
                          title={`${day.count} applications`}
                        />
                        <p className="text-[10px] sm:text-xs text-gray-600 mt-2 truncate w-full text-center">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-40 sm:h-48 flex items-center justify-center text-gray-400 text-sm">
                  No application data yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Candidate List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Candidates</h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4">
              <select
                value={candidateFilter}
                onChange={(e) => setCandidateFilter(e.target.value as any)}
                className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="offered">Offered</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 min-h-[44px] text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="date">Sort by Date</option>
                <option value="stage">Sort by Stage</option>
              </select>
            </div>
          </div>

          {sortedApplications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No candidates found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block lg:hidden space-y-4">
                {applications?.map((application) => {
                  const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
                    submitted: { label: 'Submitted', bg: 'bg-gray-100', text: 'text-gray-800' },
                    reviewed: { label: 'Reviewed', bg: 'bg-yellow-100', text: 'text-yellow-800' },
                    shortlisted: { label: 'Shortlisted', bg: 'bg-indigo-100', text: 'text-indigo-800' },
                    interviewed: { label: 'Interviewed', bg: 'bg-purple-100', text: 'text-purple-800' },
                    offered: { label: 'Offered', bg: 'bg-cyan-100', text: 'text-cyan-800' },
                    hired: { label: 'Hired', bg: 'bg-green-100', text: 'text-green-800' },
                    rejected: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-800' },
                  };
                  const config = statusConfig[application.status] || { label: application.status, bg: 'bg-gray-100', text: 'text-gray-800' };
                  
                  return (
                    <div key={application.application_id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 font-medium text-sm">
                              {application.candidate_name.substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {application.candidate_name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {application.candidate_email}
                            </div>
                          </div>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text} ml-2 flex-shrink-0`}>
                          {config.label}
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-500 mb-3">
                        Applied: {new Date(application.applied_at).toLocaleDateString()}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {application.status === 'shortlisted' && (
                          <button
                            onClick={() => handleScheduleInterview(application)}
                            className="inline-flex items-center px-3 py-2 min-h-[44px] text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded border border-purple-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {interviewsMap[application.application_id] ? 'View Interview' : 'Schedule'}
                          </button>
                        )}
                        {application.status !== 'hired' && application.status !== 'rejected' && (
                          <button
                            onClick={() => handleAdvanceStage(application.application_id, true)}
                            className="inline-flex items-center px-3 py-2 min-h-[44px] text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Next Stage
                          </button>
                        )}
                        {application.status !== 'hired' && application.status !== 'rejected' && (
                          <button
                            onClick={() => handleStageUpdate(application.application_id, 'rejected')}
                            className="inline-flex items-center px-3 py-2 min-h-[44px] text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Reject
                          </button>
                        )}
                        <button
                          onClick={() => handleSendEmail(application.application_id)}
                          className="inline-flex items-center px-3 py-2 min-h-[44px] text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Email
                        </button>
                        <button
                          onClick={() => navigate(`/admin/candidates/${application.candidate_id}`)}
                          className="inline-flex items-center px-3 py-2 min-h-[44px] text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Profile
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Stage
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applications?.map((application) => (
                      <tr key={application.application_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 font-medium">
                                {application.candidate_name.substring(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {application.candidate_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {application.candidate_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(application.applied_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
                              submitted: { label: 'Submitted', bg: 'bg-gray-100', text: 'text-gray-800' },
                              reviewed: { label: 'Reviewed', bg: 'bg-yellow-100', text: 'text-yellow-800' },
                              shortlisted: { label: 'Shortlisted', bg: 'bg-indigo-100', text: 'text-indigo-800' },
                              interviewed: { label: 'Interviewed', bg: 'bg-purple-100', text: 'text-purple-800' },
                              offered: { label: 'Offered', bg: 'bg-cyan-100', text: 'text-cyan-800' },
                              hired: { label: 'Hired', bg: 'bg-green-100', text: 'text-green-800' },
                              rejected: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-800' },
                            };
                            const config = statusConfig[application.status] || { label: application.status, bg: 'bg-gray-100', text: 'text-gray-800' };
                            return (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
                                {config.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {/* Schedule Interview - only for shortlisted */}
                            {application.status === 'shortlisted' && (
                              <div className="relative">
                                <button
                                  onClick={() => handleScheduleInterview(application)}
                                  className="text-purple-600 hover:text-purple-900 p-1"
                                  title={interviewsMap[application.application_id] ? "View/Edit Interview" : "Schedule Interview"}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </button>
                                {interviewsMap[application.application_id] && (
                                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Advance to Next Stage - disabled for final stages */}
                            {application.status !== 'hired' && application.status !== 'rejected' && (
                              <button
                                onClick={() => handleAdvanceStage(application.application_id, true)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200"
                                title="Advance to Next Stage"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                                Next
                              </button>
                            )}
                            {/* Reject - only if not already rejected or hired */}
                            {application.status !== 'hired' && application.status !== 'rejected' && (
                              <button
                                onClick={() => handleStageUpdate(application.application_id, 'rejected')}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200"
                                title="Reject Application"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Reject
                              </button>
                            )}
                            {/* Send Email */}
                            <button
                              onClick={() => handleSendEmail(application.application_id)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Send Status Email"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </button>
                            {/* View Profile */}
                            <button
                              onClick={() => navigate(`/admin/candidates/${application.candidate_id}`)}
                              className="text-gray-600 hover:text-gray-900 p-1"
                              title="View Profile"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Social Media Modal */}
      <SocialMediaModal
        isOpen={showSocialModal}
        onClose={() => setShowSocialModal(false)}
        jobId={jobId!}
        jobTitle={analytics.job_title}
      />

      {/* Schedule Interview Modal */}
      {selectedApplication && (
        <ScheduleInterviewModal
          isOpen={showInterviewModal}
          onClose={() => {
            setShowInterviewModal(false);
            setSelectedApplication(null);
          }}
          applicationId={selectedApplication.application_id}
          candidateName={selectedApplication.candidate_name}
          candidateEmail={selectedApplication.candidate_email}
          jobTitle={job?.title || ''}
          existingInterview={selectedApplication.existingInterview}
        />
      )}
    </AdminLayout>
  );
}
